// Sticker picker: the server's stickers, searchable by name. Picking one sends it at once (like a GIF).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { stickerUrl, type Sticker } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { useServerEmoji } from "../customEmoji.tsx";
import "./emoji-admin.css";
import { dropKeyboard } from "../mobile.ts";

export function StickerPicker({ onPick, onClose, embedded }: { onPick: (s: Sticker) => void; onClose: () => void; /** Inside MediaPicker's tabs: it places, closes and sheets the panel. */ embedded?: boolean }) {
  const { serverId, stickers } = useServerEmoji();
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedded) return;
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? stickers.filter((x) => x.name.toLowerCase().includes(s)) : stickers;
  }, [stickers, q]);

  const sheet = isNarrow();
  const search = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (!sheet) search.current?.focus({ preventScroll: true });
  }, []);
  const panel = (
    <div className={`gif-picker sticker-picker${sheet ? " in-sheet" : ""}${embedded ? " embedded" : ""}`} ref={box} role={embedded ? "tabpanel" : "dialog"} aria-label="Stickers">
      <div className="emoji-search">
        <label htmlFor="sticker-q" className="sr-only">Search stickers</label>
        <input id="sticker-q" ref={search} className="input" placeholder="Search stickers" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="gif-grid-wrap sheet-scroll" onTouchStart={dropKeyboard}>
        {stickers.length === 0 && <p className="muted" style={{ padding: 12, fontSize: 13 }}>This server has no stickers yet. Owners and admins add them in Server settings → Stickers.</p>}
        {stickers.length > 0 && shown.length === 0 && <p className="muted" style={{ padding: 12, fontSize: 13 }}>No stickers match “{q}”.</p>}
        <div className="sticker-grid">
          {shown.map((s) => (
            <button key={s.id} type="button" className="sticker-cell" onClick={() => onPick(s)} title={s.name} aria-label={`Send sticker ${s.name}`}>
              <img src={stickerUrl(serverId, s.id)} alt="" loading="lazy" draggable={false} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  if (embedded) return panel;
  return sheet ? (
    <Sheet label="Stickers" onClose={onClose}>
      {panel}
    </Sheet>
  ) : (
    panel
  );
}
